import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Coins, X, TrendingUp, ChevronRight, Wallet } from 'lucide-react';
import { useAuctionSounds } from '../../hooks/useAuctionSounds';

export type PaymentSource = 'digital' | 'physical' | 'dating' | 'tip' | 'subscription' | 'auction' | 'payout' | 'general';

interface CreatorPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount?: number | string;
  source?: PaymentSource;
  title?: string;
  message?: string;
}

const SOURCE_LABELS: Record<PaymentSource, { label: string; color: string; detail: string }> = {
  digital:      { label: 'Digital Prize Delivered',   color: 'text-cyan-400',    detail: 'Winner claimed the digital item' },
  physical:     { label: 'Delivery Confirmed',         color: 'text-blue-400',    detail: 'Buyer confirmed delivery receipt' },
  dating:       { label: 'Creator Services Completed', color: 'text-pink-400',    detail: 'Meeting code verified successfully' },
  tip:          { label: 'Tip Received',               color: 'text-amber-400',   detail: 'A fan sent you a tip' },
  subscription: { label: 'Subscription Payment',       color: 'text-purple-400',  detail: 'New subscription payment received' },
  auction:      { label: 'Game-a-Rama Earnings',           color: 'text-emerald-400', detail: 'Game-a-Rama sale completed' },
  payout:       { label: 'Payout Approved',            color: 'text-green-400',   detail: 'Your withdrawal has been processed' },
  general:      { label: 'Payment Received',           color: 'text-emerald-400', detail: 'Funds added to your wallet' },
};

// ─── Falling $ particle ───────────────────────────────────────────────────────
const DollarParticle: React.FC<{ index: number }> = ({ index }) => {
  const left = `${5 + ((index * 37) % 90)}%`;
  const duration = 1.6 + (index % 4) * 0.3;
  const symbols = ['$', '¢', '$', '💰'];
  return (
    <motion.div
      className="absolute text-sm sm:text-base text-emerald-400 font-black pointer-events-none select-none"
      style={{ left, top: '-20px' }}
      initial={{ y: 0, opacity: 1, rotate: 0 }}
      animate={{ y: 450, opacity: 0, rotate: (index % 2 === 0 ? 1 : -1) * 270 }}
      transition={{ duration, delay: index * 0.08, ease: 'easeIn' }}
    >
      {symbols[index % symbols.length]}
    </motion.div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const CreatorPaymentModal: React.FC<CreatorPaymentModalProps> = ({
  isOpen, onClose, amount, source = 'general', title, message,
}) => {
  const navigate = useNavigate();
  const { play } = useAuctionSounds(true);
  const soundPlayedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      soundPlayedRef.current = false;
      return;
    }
    if (soundPlayedRef.current) return;
    soundPlayedRef.current = true;
    const t = setTimeout(() => play('money_credited'), 200);
    return () => clearTimeout(t);
  }, [isOpen, play]);

  const info = SOURCE_LABELS[source] ?? SOURCE_LABELS.general;

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* ── Backdrop ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 backdrop-blur-md z-[9998]"
            aria-hidden="true"
            onClick={onClose}
          />

          {/* ── Modal sheet ── */}
          <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 mobile-modal-viewport">
            <motion.div
              initial={{ opacity: 0, y: '100%', scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: '100%', scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              className="
                relative w-full overflow-hidden
                rounded-t-3xl sm:rounded-3xl
                max-h-[92dvh] sm:max-h-[90vh] mobile-modal-sheet
                sm:w-[min(420px,calc(100vw-2rem))]
                shadow-2xl
              "
              role="dialog"
              aria-modal="true"
              aria-label="Payment Credited"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Falling particles */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
                {Array.from({ length: 14 }, (_, i) => <DollarParticle key={i} index={i} />)}
              </div>

              {/* Background — dark always */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#032b1a] via-[#0d1f1a] to-[#0d1333]" />

              {/* Emerald accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-600 via-teal-300 to-emerald-600" />

              {/* Drag handle (mobile) */}
              <div className="relative z-20 flex justify-center pt-3 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-white/30" />
              </div>

              {/* Content */}
              <div className="relative z-20 px-5 pt-2 sm:px-6 sm:pt-4 overflow-y-auto max-h-[90dvh] sm:max-h-none mobile-modal-scroll mobile-modal-footer">
                {/* Close */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-4 sm:top-4 sm:right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center transition-colors touch-manipulation"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-white" />
                </button>

                {/* Icon */}
                <motion.div
                  animate={{ scale: [1, 1.07, 1], rotate: [-3, 3, -3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mx-auto mb-3 sm:mb-4 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.55)]"
                >
                  <Coins className="w-10 h-10 sm:w-12 sm:h-12 text-white drop-shadow-lg" />
                </motion.div>

                {/* Headline */}
                <motion.h2
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                  className="text-center text-xl sm:text-2xl font-black text-white tracking-tight mb-1"
                >
                  {title ?? 'MONEY CREDITED!'}
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.28 }}
                  className={`text-center font-semibold text-xs sm:text-sm mb-4 ${info.color}`}
                >
                  {info.label}
                </motion.p>

                {/* Amount card */}
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-3.5 sm:p-4 mb-4"
                >
                  {amount && (
                    <div className="mb-3 text-center">
                      <p className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider mb-1">Amount Credited</p>
                      <p className="text-emerald-400 font-black text-3xl sm:text-4xl leading-none">
                        {typeof amount === 'number'
                          ? `${amount.toLocaleString()} tokens`
                          : amount}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 justify-center">
                    <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <p className="text-white/65 text-xs sm:text-sm text-center leading-snug">
                      {message ?? info.detail}
                    </p>
                  </div>
                </motion.div>

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.48 }}
                  className="flex flex-col gap-3"
                >
                  <button
                    onClick={() => { onClose(); navigate('/dashboard'); }}
                    className="
                      w-full py-3.5 sm:py-4 rounded-2xl
                      bg-gradient-to-r from-emerald-500 to-teal-600
                      hover:from-emerald-400 hover:to-teal-500
                      active:scale-[0.98]
                      text-white font-black text-base sm:text-lg
                      transition-all shadow-lg shadow-emerald-500/30
                      flex items-center justify-center gap-2
                      touch-manipulation
                    "
                  >
                    <Wallet className="w-5 h-5 flex-shrink-0" />
                    View Earnings
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onClose}
                    className="
                      w-full py-3 sm:py-3.5 rounded-2xl
                      bg-white/10 hover:bg-white/15 active:bg-white/20
                      text-white/80 font-semibold text-sm sm:text-base
                      transition-all touch-manipulation
                    "
                  >
                    Dismiss
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};

export default CreatorPaymentModal;
